import { useMemo } from 'react';

import type { CourseMetadata } from 'types';
import type { CourseInfo } from 'types/course';
import type ProgressStore from 'stores/Progress';

type UseCourseLandingContentOptions = {
  courseSlug: string | undefined;
  courseInfo: CourseInfo | null;
  courseMetadata: CourseMetadata | null;
  language: string;
  translations: string[] | null;
  progressStore: ProgressStore;
  isBundleCourse: boolean;
};

export default function useCourseLandingContent({
  courseSlug,
  courseInfo,
  courseMetadata,
  language,
  translations,
  progressStore,
  isBundleCourse,
}: UseCourseLandingContentOptions) {
  const firstVideoEntry = useMemo(() => {
    if (!courseInfo?.page_videos) return undefined;

    const firstPageEntries = courseInfo.page_videos.find(
      (v) => v.page_slug === 'pr01',
    );

    if (!firstPageEntries) return undefined;

    const languageVideo = firstPageEntries.entries.find(
      (e) => e.language === language,
    );

    return (
      languageVideo ??
      firstPageEntries.entries.find((e) => e.language === 'en-us')
    );
  }, [courseInfo, language]);

  const contentForLang = useMemo(() => {
    if (!courseMetadata) return null;

    const description =
      courseMetadata.description?.[language] ??
      courseMetadata.description?.['en-US'];
    const prerequisites =
      courseMetadata.prerequisites?.[language] ??
      courseMetadata.prerequisites?.['en-US'];
    const objectives =
      courseMetadata.objectives?.[language] ??
      courseMetadata.objectives?.['en-US'];

    return {
      description,
      prerequisites,
      objectives,
    };
  }, [courseMetadata, language]);

  const totalVideos = useMemo(
    () => courseInfo?.page_videos?.length ?? null,
    [courseInfo],
  );

  const progress =
    progressStore.getProgressForCourse(courseSlug)?.total_progress || 0;
  const formattedProgress = (progress * 100).toFixed();

  const nothingToShow =
    !isBundleCourse &&
    !courseMetadata &&
    !translations &&
    !contentForLang?.description &&
    !contentForLang?.objectives?.length &&
    !contentForLang?.prerequisites?.length;

  return {
    firstVideoEntry,
    contentForLang,
    totalVideos,
    progress,
    formattedProgress,
    nothingToShow,
  };
}
